<template>
  <v-form id="intervention_form" v-model="valid" ref="form" lazy-validation>
    <v-container fluid>
      <v-layout row wrap>
        <v-flex xs12>
          <v-select
            attach
            name="categoryId"
            id="createIntervention-category"
            content-class="createIntervention-content-category"
            :items="interventionFields"
            v-model="interventionCategorySelected"
            :rules="requiredRule"
            required
            :label="$t('intervention_category')">
          </v-select>
        </v-flex>
        <v-flex xs12>
          <v-select
            attach
            name="studentId"
            id="createIntervention-student"
            content-class="createIntervention-content-student"
            :label="`${ $t('student.student') }`"
            autocomplete
            :filter="x => x"
            :loading="loading"
            required
            :items="students"
            :rules="requiredRule"
            :search-input.sync="searchStudents"
            @change="studentChange"
            v-model="selectedStudent">
          </v-select>
        </v-flex>

        <v-flex xs12>
          <span class="subheading">{{$t('intervention_description_text')}}</span>
          <v-text-field
            id="createIntervention-description"
            name="description"
            multi-line
            :label="$t('intervention_description_label')"
            required
            :rules="requiredRule"
            v-model="description"
          >
          </v-text-field>
        </v-flex>


        <v-flex xs12>
          <span class="subheading">{{$t('intervention_communication_text')}}</span>
          <v-select
            attach
            name="communicationApproachId"
            id="createIntervention-communication"
            content-class="createIntervention-content-communication"
            :items="communicationFields"
            v-model="selectedCommunication"
            :label="$t('communication')"
            required
            :rules="requiredRule"
          >
          </v-select>
        </v-flex>
        <v-flex xs12>
          <span class="subheading">{{$t('intervention_communication_date')}}</span>
          <v-menu id="createIntervention-communication-date"
                  lazy
                  :close-on-content-click="false"
                  v-model="communicationPickerMenu"
                  transition="scale-transition"
                  offset-y
                  nudge-left="8"
                  full-width
                  attach
                  max-width="290px"
                  min-width="290px">
            <v-text-field
              slot="activator"
              v-model="communicationDateFormatted"
              prepend-icon="event"
              @blur="communicationDate = parseDate(communicationDateFormatted)"
              readonly>
            </v-text-field>
            <v-date-picker
              id="createIntervention-communication-date-picker"
              v-model="communicationDate"
              :locale="currentLocale"
              @input="communicationDateSelected"
              scrollable
              :allowed-dates="allowDatesTodayOrBefore"
            >
            </v-date-picker>
          </v-menu>
        </v-flex>
        <v-flex xs12>
          <span class="subheading">{{$t('intervention_action_text')}}</span>
          <v-text-field
            name="studentAction"
            id="createIntervention-action"
            :label="$t('action')"
            multi-line
            v-model="action"
          >
          </v-text-field>
        </v-flex>
        <v-flex xs12 pb-4>
          <div class="subheading">{{$t('intervention_duration_text')}}</div>
          <vue-slider :min="meetingSliderMin" :max="meetingSliderMax" :value="meetingDuration" :interval="10"
                      :show="showSlider" :formatter="formatTooltip"
                      :processStyle="sliderStyle" :tooltipStyle="tooltipStyle" tooltip="hover" v-on:input="sliderInput"
                      :piecewise="false" :dot-size="28" :piecewiseLabel="true">
          </vue-slider>
          <v-flex pt-3>
            <v-text-field
              :label="$t('intervention_form_duration_amount', meetingDuration, meetingDuration)"
              :min="meetingSliderMin"
              :max="meetingSliderMax"
              mask="##"
              type="tel"
              step="1"
              v-model="meetingDuration"
            />
          </v-flex>
        </v-flex>
        <v-flex xs12>
          <span class="subheading">{{$t('intervention_date_text')}}</span>
          <v-menu id="createIntervention-date"
                  lazy
                  :close-on-content-click="false"
                  v-model="pickerMenu"
                  transition="scale-transition"
                  offset-y
                  nudge-left="8"
                  full-width
                  attach
                  max-width="290px"
                  min-width="290px"
          >
            <v-text-field
              slot="activator"
              :label="$t('intervention_date_label')"
              v-model="dateFormatted"
              prepend-icon="event"
              @blur="date = parseDate(dateFormatted)"
              readonly>
            </v-text-field>
            <v-date-picker
              id="createIntervention-date-picker"
              v-model="date"
              :locale="currentLocale"
              @input="dateSelected"
              scrollable
              :allowed-dates="allowedDates"
            >
            </v-date-picker>
          </v-menu>
        </v-flex>

        <v-flex xs12>
          <v-layout row wrap>
            <v-flex pt-4>
              <span class="subheading">{{$t('intervention_reminder_text')}}</span>
            </v-flex>
            <v-flex>
              <v-select
                attach
                id="createIntervention-reminder"
                content-class="createIntervention-content-reminder"
                name="reminderEmailFrequency"
                :items="dayFields"
                v-model="reminderInDays"
              >
              </v-select>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex xs12>
          <v-checkbox name="repeatingEmail" v-if="reminderInDays > 0"
                      class="createIntervention-repeating"
                      v-model="repeatingEmail"
                      :label="$t('intervention_referral_repeating_email')"
          >
          </v-checkbox>
        </v-flex>
        <v-flex xs12>
          <span class="subheading">{{$t('intervention_referral_text')}}</span>
          <v-select
            attach
            id="createIntervention-referral"
            content-class="createIntervention-content-referral"
            name="refereeId"
            :label="$t('referral.title')"
            :items="referralFields"
            v-model="referralSelected"
            max-height="240"
          >
          </v-select>
        </v-flex>

        <v-flex v-if="isUrlTypeReferral" xs12>
          <span class="subheading">{{$t('referral.url_label')}}</span>
          <a target="_blank" :href="currentReferral.url" class="prevent-overflow">
            {{$t(currentReferral.urlKey) || $t(currentReferral.text)}}</a>
        </v-flex>
        <v-flex v-else-if="isEmailTypeReferral" xs12>
          <v-text-field
            id="createIntervention-referralSummary"
            content-class="createIntervention-content-referralSummary"
            name="referralSummary"
            :label="$t('referral_summary_label')"
            v-model="summary"
            multi-line
          >
          </v-text-field>
        </v-flex>

        <v-flex xs12 py-3 v-if="flagReferralConsent && referralSelected">
            <v-checkbox slot="activator" name="consent"
                        class="createIntervention-consent"
                        v-model="consent"
                        :label="$t('intervention_consent')"
            />
        </v-flex>

        <v-flex xs12 v-show="!interventionId">
            <v-checkbox slot="activator" name="closeOnCreation"
                        class="createIntervention-close"
                        v-model="closeOnCreation"
                        :label="$t('intervention_close_on_creation')"
            />
        </v-flex>

        <v-flex v-if="isEmailTypeReferral" xs12>
          <span class="subheading">{{$t('intervention_recipients.sub_header')}}</span>
          <combo-box :id="`recipient`" :items="translatedRecipientsTypes"/>
        </v-flex>

        <v-flex xs12 my-3 class="caption">
          *{{$t('required_fields')}}
        </v-flex>
      </v-layout>

      <v-layout row justify-center>
        <v-btn
          id="createIntervention-cancel"
          @click="cancel"
          :disabled="sending">
          {{$t('cancel')}}
        </v-btn>
        <transition name="wiggle">
          <v-btn
            id="createIntervention-create"
            @click="createIntervention"
            color="primary"
            :disabled="flagReferralConsent ? ((referralSelected && !consent) || sending) : sending"
            :loading="sending"
            v-if="sendingShow">
            <span v-if="!interventionId">{{$t('create')}}</span>
            <span v-if="interventionId">{{$t('update')}}</span>
          </v-btn>
        </transition>
      </v-layout>
    </v-container>
  </v-form>
</template>

<script>
  import moment from 'moment-es6'
  import { mapActions, mapGetters, mapState } from 'vuex'
  import {
    SET_INTERVENTION_ACTION,
    SET_INTERVENTION_CATEGORY,
    SET_INTERVENTION_CLOSE_ON_CREATE,
    SET_INTERVENTION_CONSENT,
    SET_INTERVENTION_COMMUNICATION,
    SET_INTERVENTION_COMMUNICATION_DATE,
    SET_INTERVENTION_COMMUNICATION_DATE_FORMATTED,
    SET_INTERVENTION_DATE,
    SET_INTERVENTION_DATE_FORMATTED,
    SET_INTERVENTION_DESCRIPTION,
    SET_INTERVENTION_DURATION,
    SET_INTERVENTION_FORM_VALID,
    SET_INTERVENTION_REFERRAL,
    SET_INTERVENTION_REMINDER_DAYS,
    SET_INTERVENTION_REPEATING_EMAIL,
    SET_INTERVENTION_STUDENT,
    SET_INTERVENTION_STUDENTS,
    SET_INTERVENTION_SUMMARY,
    DECREMENT_SSO_REFRESH_FREQUENCY
  } from '../../store/mutation-types'
  import { GET_INTERVENTION_STUDENTS_LIST, POST_INTERVENTION_FORM, REFRESH_SSO_TOKEN } from '../../store/action-types'
  import { getRange } from '../../HelperMethods'
  import { GET_SELECTED_REFERRAL_OBJECT, HAS_FEATURE_FLAG } from '../../store/getter-types'
  import Helpers from '../../mixins/Helpers'
  import VueScrollTo from 'vue-scrollto'
  import vueSlider from 'vue-slider-component'
  import TooltipStyle from '../../mixins/TooltipStyle'
  import ComboBox from './ComboBox'

  export default {
    name: 'intervention-form',
    props: ['cancel'],
    mixins: [Helpers, TooltipStyle],
    components: {ComboBox, vueSlider},
    data () {
      // Delay, else may fail to work.
      setTimeout(() => {
        this.showSlider = true
      }, 500)

      return {
        snackbarTimeout: 2500,
        snackBarShow: true,
        requiredRule: [v => !!v || this.$t('field_required')],
        searchStudents: null,
        days: getRange(1, 7),
        pickerMenu: false,
        communicationPickerMenu: false,
        sendingShow: true,
        showSlider: false,
        meetingSliderMax: 60,
        meetingSliderMin: 0
      }
    },
    watch: {
      searchStudents (val) {
        val && this.queryStudents(val)
      }
    },
    methods: {
      formatTooltip (val) {
        return val === this.meetingSliderMax ? `${val}+` : val
      },
      ...mapActions({
        queryStudents: GET_INTERVENTION_STUDENTS_LIST
      }),
      createIntervention () {
        if (this.$refs.form.validate()) {
          let payload = {}

          // Gets most of the grunt work done. For simple fields just add name='server_key' to the form element.
          this.$refs.form.getInputs()
            .filter(field => field.$attrs.name !== undefined)
            .forEach((inputField) => {
              payload[inputField.$attrs.name] = inputField.value || inputField.inputValue
            })

          payload['actionTargetDate'] = this.date
          payload['meetingDuration'] = this.meetingDuration
          payload['dateOfCommunication'] = this.communicationDate
          payload['consented'] = this.consent
          payload['recipients'] = this.buildRecipients
          this.$store.dispatch(POST_INTERVENTION_FORM, payload)
        } else {
          // Makes the button wiggle around due to the animation associated with it
          this.sendingShow = false
          this.$nextTick(() => {
            this.sendingShow = true
          })
          // Scroll to the first element with a validation error
          for (let i = 0; i < this.$refs.form.getInputs().length; i++) {
            if (this.$refs.form.getInputs()[i].hasError) {
              let options = {
                container: '#right-drawer',
                easing: 'ease-in-out',
                offset: -64,
                cancelable: false
              }
              VueScrollTo.scrollTo(this.$refs.form.getInputs()[i].$el, 500, options)
              break
            }
          }
        }
      },
      allowedDates (date) {
        // Allows today and in the future
        const now = moment()
        const then = moment(date).startOf('day')
        return then.diff(now, 'days') >= 0
      },
      allowDatesTodayOrBefore (date) {
        // Allows today and in the past
        const now = moment().startOf('day')
        const then = moment(date)
        return then.diff(now, 'days') <= 0
      },
      formatDate (date) {
        if (!date) {
          return null
        }
        return moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY')
      },
      parseDate (date) {
        if (!date) {
          return null
        }
        return moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD')
      },
      dateSelected (value) {
        this.dateFormatted = this.formatDate(value)
        this.pickerMenu = false
      },
      communicationDateSelected (value) {
        this.communicationDateFormatted = this.formatDate(value)
        this.communicationPickerMenu = false
      },
      studentChange (data) {
        /* data is different types
             number - a valid student
             event - an unknown student name.
           The issue was unknown names appeared blank without a validation error.
         */
        if (data.srcElement) {
          this.selectedStudent = ''
        }
      },
      sliderInput (value) {
        this.meetingDuration = Number(value)
      },
      refreshSSOToken () {
        this.$store.dispatch(REFRESH_SSO_TOKEN)
        this.$store.commit(DECREMENT_SSO_REFRESH_FREQUENCY)
        if (this.ssoRefreshAttempts < 1) {
          clearInterval(this.interval1)
        }
      },
      checkSSOStatus () {
        this.interval1 = setInterval(function () {
          this.refreshSSOToken()
        }.bind(this), this.ssoRefreshFrequency)
      }
    },
    computed: {
      ...mapState({
        recipientTypes: state => state.intervention.recipientTypes,
        originalRecipientTypes: state => state.intervention.originalRecipientTypes,
        selectedUserRecipients: state => state.intervention.selectedUserRecipients,
        communications: state => state.intervention.communications,
        categories: state => state.intervention.categories,
        referrals: state => state.intervention.referrals,
        loading: state => state.intervention.loading,
        sending: state => state.intervention.sending,
        interventionId: state => state.intervention.interventionId,
        currentLocale: state => state.i18n.locale,
        creationDate: state => state.intervention.creationDate,
        moduleInterExem: state => state.profile.module.interexem,
        functionInterventionForm: state => state.intervention.functions.interventionForm,
        optionSingleInterventionCategory: state => state.intervention.options.enable.singleInterventionCategory,
        optionReferralConsent: state => state.intervention.options.enable.referralConsent,
        ssoRefreshFrequency: state => state.config.ssoRefreshFrequency,
        ssoRefreshAttempts: state => state.config.ssoRefreshAttempts
      }),
      translatedRecipientsTypes () {
        return this.translateFields(this.recipientTypes)
      },
      originalValues () {
        return this.originalRecipientTypes.map(el => el.value)
      },
      buildRecipients () {
        return this.selectedUserRecipients.map(el => {
          if (this.originalValues.includes(el)) {
            return {
              type: el,
              email: null
            }
          } else {
            return {
              type: 'FREESTYLE',
              email: el
            }
          }
        })
      },
      meetingDurationPlus () {
        return this.meetingDuration === this.meetingSliderMax ? `${this.meetingDuration}+` : this.meetingDuration
      },
      ...mapGetters({
        currentReferral: GET_SELECTED_REFERRAL_OBJECT,
        hasFeatureFlag: HAS_FEATURE_FLAG
      }),
      dayFields () {
        let dayFields = []
        dayFields.push({
          'text': this.$t('email_no_reminder'),
          'value': 0
        })
        this.days.forEach(day => {
          const fieldText = this.$t('days', {day: day}, day)
          dayFields.push({
            'text': fieldText,
            'value': day
          })
        })
        return dayFields
      },
      interventionFields () {
        return this.translateFields(this.categories)
      },
      communicationFields () {
        return this.translateFields(this.communications)
      },
      referralFields () {
        return this.translateFields(this.referrals)
      },
      interventionCategorySelected: {
        get () {
          if (this.flagSingleInterventionCategory && this.interventionFields.length === 1) {
            return this.interventionFields[0].value
          } else {
            return this.$store.state.intervention.interventionCategorySelected
          }
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_CATEGORY, value)
        }
      },
      selectedStudent: {
        get () {
          return this.$store.state.intervention.selectedStudent
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_STUDENT, value)
        }
      },
      selectedCommunication: {
        get () {
          if (this.flagSingleInterventionCategory && this.communicationFields.length === 1) {
            return this.communicationFields[0].value
          } else {
            return this.$store.state.intervention.selectedCommunication
          }
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_COMMUNICATION, value)
        }
      },
      reminderInDays: {
        get () {
          return this.$store.state.intervention.reminderInDays
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_REMINDER_DAYS, value)
        }
      },
      repeatingEmail: {
        get () {
          return this.$store.state.intervention.repeatingEmail
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_REPEATING_EMAIL, value)
        }
      },
      date: {
        get () {
          return this.$store.state.intervention.date
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_DATE, value)
        }
      },
      dateFormatted: {
        get () {
          return this.$store.state.intervention.dateFormatted
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_DATE_FORMATTED, value)
        }
      },
      communicationDate: {
        get () {
          return this.$store.state.intervention.dateOfCommunication
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_COMMUNICATION_DATE, value)
        }
      },
      communicationDateFormatted: {
        get () {
          return this.$store.state.intervention.dateOfCommunicationFormatted
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_COMMUNICATION_DATE_FORMATTED, value)
        }
      },
      action: {
        get () {
          return this.$store.state.intervention.action
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_ACTION, value)
        }
      },
      summary: {
        get () {
          return this.$store.state.intervention.summary
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_SUMMARY, value)
        }
      },
      students: {
        get () {
          return this.$store.state.intervention.students
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_STUDENTS, value)
        }
      },
      valid: {
        get () {
          return this.$store.state.intervention.valid
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_FORM_VALID, value)
        }
      },
      referralSelected: {
        get () {
          return this.$store.state.intervention.referralSelected
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_REFERRAL, value)
        }
      },
      meetingDuration: {
        get () {
          return this.$store.state.intervention.meetingDuration
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_DURATION, value)
        }
      },
      description: {
        get () {
          return this.$store.state.intervention.description
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_DESCRIPTION, value)
        }
      },
      closeOnCreation: {
        get () {
          return this.$store.state.intervention.closeOnCreation
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_CLOSE_ON_CREATE, value)
        }
      },
      consent: {
        get () {
          return this.$store.state.intervention.consent
        },
        set (value) {
          this.$store.commit(SET_INTERVENTION_CONSENT, value)
        }
      },
      isUrlTypeReferral () {
        return this.currentReferral ? this.currentReferral.url : false
      },
      isEmailTypeReferral () {
        return this.currentReferral ? (this.currentReferral.value && !this.currentReferral.url) : false
      },
      flagSingleInterventionCategory () {
        return this.hasFeatureFlag(this.moduleInterExem + '.' + this.functionInterventionForm + '.' + this.optionSingleInterventionCategory)
      },
      flagReferralConsent () {
        return this.hasFeatureFlag(this.moduleInterExem + '.' + this.functionInterventionForm + '.' + this.optionReferralConsent)
      }
    },
    mounted () {
      if (this.ssoRefreshAttempts > 0 && this.ssoRefreshFrequency > 0) {
        this.checkSSOStatus()
      }
    }
  }
</script>

<style scoped>
  .wiggle-enter-active, .wiggle-leave-active {
    animation: wiggle .2s infinite;
  }

  .createIntervention-consent /deep/ label {
    height: 56px;
    white-space: unset;
  }

  @keyframes wiggle {
    0% {
      right: 0;
    }
    33% {
      right: 5px;
    }
    66% {
      right: -5px;
    }
    100% {
      right: 0;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/intervention/InterventionForm.vue